import { getFrontendConfigValue, useToast } from '@arnold/common';
import { ReactComponent as DeprecatedCalendarIcon } from '@arnold/common/src/assets/icons/DeprecatedCalendarIcon.svg';
import { ReactComponent as CompanyIcon } from '@arnold/common/src/assets/icons/CompanyIcon.svg';
import { ReactComponent as SettingsIcon } from '@arnold/common/src/assets/icons/SettingsIcon.svg';
import { ReactComponent as SurveyIcon } from '@arnold/common/src/assets/icons/SurveyIcon.svg';
import { ReactComponent as TeamIcon } from '@arnold/common/src/assets/icons/TeamIcon.svg';
import classNames from 'classnames';
import PerfectScrollbar from 'perfect-scrollbar';
import { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import packageJson from '../../../package.json';
import { IsAccessTokenValidQuery } from '../../generated/hooks';
import { OrganizationLicence, RouteType } from '../../lib/types';
import { ScreenWrapper } from '../Common';
import { InformationFooter } from '../index';
import Navbar from './Navbar';
import SideBar from './SideBar';

interface IProps extends RouteComponentProps<any> {
  children: ReactElement;
  organizationName: string;
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
  organizationLicence?: OrganizationLicence;
  allowedMoreOrganizations: boolean;
  product?: string;
  hasProcessSurveys: boolean;
  hasPulserModule: boolean;
  leftNavbarOnly?: boolean;
  hideSidebar?: boolean;
  organizationId?: string | null;
}

function AuthorizedPage({
  children,
  organizationName,
  organizationLicence,
  hasProcessSurveys,
  hasPulserModule,
  leftNavbarOnly,
  allowedMoreOrganizations,
  product,
  user,
  hideSidebar,
  organizationId,
  location,
}: IProps) {
  const [t] = useTranslation('AuthorizedPage');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();
  const [needsUpdate, setNeedsUpdate] = useState(false);

  // hook to check the app version on route change
  function useVersionCheck() {
    // check if a version refresh needed on route change
    const location = useLocation();
    useLayoutEffect(() => {
      // if there is an update available
      if (needsUpdate) {
        setNeedsUpdate(false);
        window.location.reload(); // refresh the browser
      }
    }, [location]);
  }

  useVersionCheck();

  useLayoutEffect(() => {
    let ps: PerfectScrollbar | null = null;
    if (navigator.platform.indexOf('Win') > -1 && wrapperRef.current != null) {
      document.documentElement.className += ' perfect-scrollbar-on';
      document.documentElement.classList.remove('perfect-scrollbar-off');
      ps = new PerfectScrollbar(wrapperRef.current);
    }
    return function destroy() {
      if (ps) {
        ps.destroy();
        document.documentElement.className += ' perfect-scrollbar-off';
        document.documentElement.classList.remove('perfect-scrollbar-on');
      }
    };
  });

  useEffect(() => {
    // If the page was reloaded because company was switched in other tab, show a toast
    if (organizationName && localStorage.getItem('pageReloaded') === 'true') {
      localStorage.removeItem('pageReloaded');
      addToast(t('pageReloadedToast', { orgName: organizationName }));
    }

    // Check if user switched org in another tab every time he switches tabs or every 5 minutes
    let interval: any;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        checkIfOrgHasChanged(user.id, organizationId, setNeedsUpdate);
        clearInterval(interval);
        interval = setInterval(() => checkIfOrgHasChanged(user.id, organizationId, setNeedsUpdate), 5 * 60 * 1000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    interval = setInterval(() => checkIfOrgHasChanged(user.id, organizationId, setNeedsUpdate), 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [addToast, organizationId, organizationName, t, user.id]);

  const routes: RouteType[] = [
    {
      name: t('adminOverview'),
      path: '/overview',
      iconSvg: <DeprecatedCalendarIcon />,
      isVisible: true,
      icomTag: 'nav-overview',
    },
    {
      name: t('surveys'),
      path: '/onboarding',
      iconSvg: <SurveyIcon />,
      isVisible: true,
      icomTag: 'nav-surveys',
      children: [
        {
          name: t('pulser'),
          path: '/pulser',
          isVisible: hasPulserModule,
          icomTag: 'nav-pulser',
        },
        {
          name: t('onboarding'),
          path: '/onboarding',
          isVisible: true,
          icomTag: 'nav-onboarding',
        },
        {
          name: t('exit'),
          path: '/exit',
          isVisible: true,
          icomTag: 'nav-exit',
        },
        {
          name: t('processSurveys'),
          path: '/processSurveys',
          isVisible: hasProcessSurveys,
          icomTag: 'nav-process-surveys',
        },
        {
          name: t('standaloneSurveys'),
          path: '/surveys',
          isVisible: true,
          icomTag: 'nav-standalone-surveys',
        },
        {
          name: t('templates'),
          path: '/topics',
          isVisible: true,
          icomTag: 'nav-templates',
        },
        {
          name: t('archive'),
          path: '/surveyArchive',
          isVisible: true,
          icomTag: 'nav-archive',
        },
      ],
    },
    {
      name: t('organizationalStructure'),
      path: '/teams',
      iconSvg: <TeamIcon />,
      isVisible: true,
      icomTag: 'nav-organizational-structure',
    },
    {
      name: t('settings'),
      path: '/settings',
      iconSvg: <SettingsIcon />,
      isVisible: true,
      icomTag: 'nav-settings',
    },
    {
      name: t('organizations'),
      path: '/organizations',
      iconSvg: <CompanyIcon />,
      isVisible: allowedMoreOrganizations,
      icomTag: 'nav-organizations',
    },
  ];

  const scrollBar = PAGES_WITH_CONSTANT_SCROLLBAR.includes(location.pathname.split('/')[1]);

  return (
    <ScreenWrapper
      className="wrapper"
      data-cy="admin-page"
      id="admin-page-wrapper"
      style={{ overflowY: scrollBar ? 'scroll' : undefined }}
    >
      {!hideSidebar && (
        <SideBar
          routes={routes}
          organizationLicence={organizationLicence}
          product={product}
          organizationName={organizationName}
        />
      )}
      <div className={classNames('main-panel', hideSidebar && 'main-panel--without-sidebar')} ref={wrapperRef}>
        {!leftNavbarOnly && <Navbar user={user} />}
        <div className={leftNavbarOnly ? 'no-padding-content' : 'content'}>{children}</div>
        {!leftNavbarOnly && <InformationFooter />}
      </div>
    </ScreenWrapper>
  );
}

export default withRouter(AuthorizedPage);

// check if user switched org in another tab and refresh the page
const checkIfOrgHasChanged = async (
  userId: string | undefined,
  orgId: string | null | undefined,
  setNeedsUpdate: (needsUpdate: boolean) => void,
) => {
  if (!userId || !orgId) return;
  const isUserUnderCorrectOrg = await fetch(
    `${getFrontendConfigValue('API_URL')}/check-user-under-correct-org?orgId=${orgId}`,
    { credentials: 'include' },
  )
    .then((r) => {
      if (r.headers.get('x-version') !== packageJson.version) {
        setNeedsUpdate(true);
      }
      return r.text();
    })
    .catch((e) => {
      console.error('Error checking if user is still under correct org', e);
    });

  if (isUserUnderCorrectOrg !== 'true') {
    localStorage.setItem('pageReloaded', 'true');
    window.location.reload();
  }
};

// Pages where we want to keep space for the scrollbar even if it is not needed
// helps to prevent content from jumping when some component inside resizes
const PAGES_WITH_CONSTANT_SCROLLBAR = ['teams'];
